import { Lock } from "lucide-react";

const samples = [
  { dept: "Engineering", sentiment: "Negative", body: "Our standups have become status theater. I haven't shipped meaningful work in three sprints." },
  { dept: "Sales", sentiment: "Mixed", body: "The new commission structure rewards short-term wins and punishes pipeline building. We'll regret it in Q3." },
  { dept: "Design", sentiment: "Negative", body: "My manager takes credit for my work in leadership reviews. I've started looking, quietly." },
  { dept: "Customer Success", sentiment: "Positive", body: "The shift to async on Wednesdays gave me back the focus to actually solve problems. More of this." },
  { dept: "People Ops", sentiment: "Negative", body: "We say 'unlimited PTO' but no one above me takes more than 10 days. The signal is loud." },
  { dept: "Marketing", sentiment: "Mixed", body: "Loved the off-site. Less keen on being asked to backfill three roles I never agreed to take on." },
  { dept: "Finance", sentiment: "Negative", body: "Layoffs were handled with empathy in the room and a spreadsheet outside it. People notice." },
  { dept: "Engineering", sentiment: "Positive", body: "First time in years I've worked somewhere code review feels like collaboration, not combat." },
];

const sentimentBadge = {
  Positive: "bg-success/10 text-success",
  Mixed: "bg-warning/15 text-warning",
  Negative: "bg-destructive/10 text-destructive",
} as const;

/**
 * Vertical marquee of anonymized sample feedback — Luminous Atrium styling.
 */
export const EditorialFeedbackTicker = () => {
  return (
    <section
      aria-labelledby="ticker-title"
      className="relative bg-background border-t border-border/60 px-4 sm:px-6 md:px-10 py-16 sm:py-20 md:py-32 overflow-hidden"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-0 opacity-60"
        style={{
          background:
            "radial-gradient(800px 460px at 0% 30%, hsl(150 30% 90% / 0.5), transparent 60%), radial-gradient(700px 420px at 100% 70%, hsl(50 60% 92% / 0.4), transparent 60%)",
        }}
      />

      <div className="max-w-7xl mx-auto relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-5">
          <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/70 backdrop-blur px-3.5 py-1.5 text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-medium text-foreground/70 shadow-[0_2px_8px_-4px_rgba(21,34,27,0.06)] mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            Live Feedback Wire
          </span>
          <h2
            id="ticker-title"
            className="font-sans font-medium text-4xl sm:text-5xl md:text-6xl lg:text-[64px] leading-[1.05] tracking-[-0.02em] text-foreground mb-5 md:mb-7"
          >
            What lands when the channel is{" "}
            <span className="font-serif italic font-light text-foreground/75">
              actually safe.
            </span>
          </h2>
          <p className="font-sans text-base md:text-lg text-muted-foreground leading-relaxed max-w-md mb-7">
            A live composite of the kind of feedback People teams receive in
            their first month. Names, departments, and details have been
            altered &mdash; the patterns have not.
          </p>
          <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/70 backdrop-blur px-4 py-2.5 text-xs font-medium text-foreground/75">
            <Lock className="h-3.5 w-3.5 text-primary" strokeWidth={2} />
            Submitter identity stripped at the database
          </div>
        </div>

        {/* Ticker */}
        <div className="lg:col-span-7">
          <div
            className="relative h-[380px] sm:h-[480px] md:h-[560px] overflow-hidden rounded-3xl border border-border/50 bg-card/60 backdrop-blur-sm shadow-[0_24px_60px_-24px_rgba(62,92,77,0.18)]"
            style={{
              maskImage:
                "linear-gradient(to bottom, transparent 0%, black 12%, black 88%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(to bottom, transparent 0%, black 12%, black 88%, transparent 100%)",
            }}
          >
            <div className="flex flex-col gap-4 md:gap-5 p-5 md:p-7 animate-marquee-up hover:[animation-play-state:paused]">
              {[...samples, ...samples].map((s, i) => (
                <article
                  key={i}
                  className="rounded-2xl border border-border/50 bg-card p-5 md:p-6 shadow-[0_2px_8px_-4px_rgba(21,34,27,0.06)] transition-all duration-300 hover:border-primary/30 hover:-translate-y-0.5"
                >
                  <header className="flex items-center justify-between mb-3 gap-3">
                    <span className="text-xs font-medium text-muted-foreground tracking-[-0.01em] truncate">
                      Anonymous &middot; {s.dept}
                    </span>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium tracking-[-0.01em] flex-shrink-0 ${sentimentBadge[s.sentiment as keyof typeof sentimentBadge]}`}
                    >
                      {s.sentiment}
                    </span>
                  </header>
                  <p className="font-sans text-base md:text-lg text-foreground leading-snug tracking-[-0.01em]">
                    &ldquo;{s.body}&rdquo;
                  </p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
